import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  InputGroup,
  InputLeftAddon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spacer,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useToast,
  VStack,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBolt,
  FaCopy,
  FaExternalLinkAlt,
  FaLink,
  FaRegFileAlt,
} from "react-icons/fa";
import { formatCurrency } from "../../../utils/formatCurrency";
import services from "../services";
import { TransactionType } from "../enums";
import { PaymentLink } from "../types";

type TransactionTypeSelection = TransactionType | "";

type PaymentLinksModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderData: any;
  onPaymentLinkCreatedForEnquiry?: () => void | Promise<void>;
};

const formatPaymentLinkDate = (value?: string) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "-";
  return value.length <= 10
    ? parsed.format("DD MMM YYYY")
    : parsed.format("DD MMM YYYY, hh:mm A");
};

const getErrorMessage = (error: any) => {
  const responseMessage =
    error?.response?.data?.message ||
    error?.response?.data?.errorMsg ||
    error?.response?.data?.error;
  if (Array.isArray(responseMessage)) return responseMessage[0];
  return responseMessage || "Failed to generate link. Please try again.";
};

const PaymentLinksModal: React.FC<PaymentLinksModalProps> = ({
  isOpen,
  onClose,
  orderData,
  onPaymentLinkCreatedForEnquiry,
}) => {
  const [transactionType, setTransactionType] =
    useState<TransactionTypeSelection>("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [links, setLinks] = useState<PaymentLink[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = useToast();
  const isInitializedRef = useRef(false);
  const [lastCreatedLinkId, setLastCreatedLinkId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (isOpen) {
      if (!isInitializedRef.current) {
        const initialLinks = orderData?.paymentLinks;
        setLinks(Array.isArray(initialLinks) ? initialLinks : []);
        setCopiedLinkId(null);
        isInitializedRef.current = true;
      }
    } else {
      isInitializedRef.current = false;
      // Reset form state when modal closes
      setTransactionType("");
      setAmount("");
      setNotes("");
      setCopiedLinkId(null);
      setLastCreatedLinkId(null);

      // Clear pending copy timer if it exists
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    }
  }, [isOpen]); // Depend only on isOpen to prevent typing resets

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const sortedLinks = useMemo(() => {
    return [...links].sort((first, second) => {
      return (
        new Date(first.createdAt).getTime() -
        new Date(second.createdAt).getTime()
      );
    });
  }, [links]);

  const validateForm = () => {
    if (!transactionType) return "Please select transaction type.";
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return "Please enter an amount greater than 0.";
    }
    if (notes.trim().length > 200) return "Notes cannot exceed 200 chars.";
    return "";
  };

  const showToast = (
    description: string,
    status: "success" | "error" | "warning" | "info",
  ) => {
    toast({
      description,
      status,
      duration: 4000,
      isClosable: true,
      position: "top",
    });
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      showToast(validationError, "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      const createdLink = await services.createPaymentLink(orderData._id, {
        transactionType: transactionType as TransactionType,
        amount: Number(amount),
        notes: notes.trim(),
      });
      setLinks((currentLinks) => [...currentLinks, createdLink]);
      const linkId = createdLink._id || createdLink.zohoPaymentLinkId;
      setLastCreatedLinkId(linkId);
      showToast("Payment link generated successfully!", "success");
      try {
        await onPaymentLinkCreatedForEnquiry?.();
      } catch {
        showToast(
          "Payment link created, but list refresh failed. Please refresh the order and check again.",
          "warning",
        );
      }
      setTransactionType("");
      setAmount("");
      setNotes("");
      requestAnimationFrame(() => {
        if (listRef.current) {
          listRef.current.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      });
    } catch (error) {
      showToast(getErrorMessage(error), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async (link: PaymentLink) => {
    try {
      await navigator.clipboard.writeText(link.url);
      const linkId = link._id || link.zohoPaymentLinkId;
      setCopiedLinkId(linkId);
      showToast("Payment link copied to clipboard!", "success");
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = setTimeout(() => {
        setCopiedLinkId(null);
      }, 2000);
    } catch {
      showToast("Unable to copy payment link.", "error");
    }
  };

  const handleOpen = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        maxW="920px"
        h="500px"
        maxH="92vh"
        borderRadius="12px"
        overflow="hidden"
        boxShadow="0 20px 60px rgba(0,0,0,0.15)"
        border="1px solid"
        borderColor="gray.200"
        display="flex"
        flexDirection="column"
      >
        {/* ── Modal Header ─────────────────────── */}
        <ModalHeader
          px={6}
          py={4}
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.200"
          flexShrink={0}
        >
          <HStack spacing={3} align="center" pr={10}>
            <Box
              bg="#e8f5ee"
              p={2}
              borderRadius="8px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon as={FaLink} color="#0b8e50" boxSize="16px" />
            </Box>
            <Box>
              <Text
                fontSize="md"
                fontWeight="700"
                color="gray.800"
                lineHeight="1.3"
              >
                Payment Links
              </Text>
              <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={0.5}>
                Manage payment links for enquiry{" "}
                <strong>#{orderData?.orderNo}</strong>
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton
          top={4}
          right={4}
          borderRadius="8px"
          _hover={{ bg: "gray.100" }}
        />

        {/* ── Modal Body ─────────────────────── */}
        <ModalBody
          p={0}
          overflow="hidden"
          flex="1"
          display="flex"
          flexDirection="column"
          onClick={() => {
            if (lastCreatedLinkId) setLastCreatedLinkId(null);
          }}
        >
          <Box
            display="flex"
            flexDirection={{ base: "column", md: "row" }}
            flex="1"
            overflow="hidden"
          >
            {/* ── Left Panel: Create New Link ─────────────────── */}
            <Box
              w={{ base: "100%", md: "40%" }}
              bg="white"
              borderRight={{ base: "none", md: "1px solid" }}
              borderBottom={{ base: "1px solid", md: "none" }}
              borderColor="gray.200"
              display="flex"
              flexDirection="column"
              overflow="hidden"
            >
              {/* Section Header */}
              <Box
                px={5}
                py={4}
                borderBottom="1px solid"
                borderColor="gray.200"
                h="72px"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Box mb={1}>
                  <Text fontSize="15px" fontWeight="700" color="gray.800">
                    Create New Link
                  </Text>
                </Box>
                <Text fontSize="12px" color="gray.500" lineHeight="1.4">
                  Fill details to generate a Zoho Books payment link.
                </Text>
              </Box>

              {/* Form */}
              <VStack
                align="stretch"
                spacing={4}
                p={5}
                overflowY="auto"
                flex="1"
              >
                {/* Customer Name (read-only display) */}
                <Box>
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color="gray.600"
                    mb={1.5}
                  >
                    Customer Name
                  </Text>
                  <Box
                    bg="gray.100"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    px={3}
                    py={1.5}
                    fontSize="sm"
                    color="gray.600"
                    minH="32px"
                    display="flex"
                    alignItems="center"
                    overflow="hidden"
                  >
                    <Tooltip
                      label={orderData?.customer?.name ?? "-"}
                      hasArrow
                      placement="top"
                    >
                      <Text isTruncated>
                        {orderData?.customer?.name ?? "-"}
                      </Text>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Transaction Type + Amount in single row */}
                <HStack spacing={3} align="flex-start">
                  <FormControl isRequired flex={1}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="600"
                      color="gray.600"
                      mb={1.5}
                    >
                      Transaction Type
                    </FormLabel>
                    <Select
                      value={transactionType}
                      onChange={(event) =>
                        setTransactionType(
                          event.target.value as TransactionTypeSelection,
                        )
                      }
                      placeholder="Select type"
                      size="sm"
                      fontSize="sm"
                      borderRadius="md"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{
                        borderColor: "#0b8e50",
                        boxShadow: "0 0 0 1px #0b8e50",
                      }}
                    >
                      <option value={TransactionType.FACILITATION}>
                        Facilitation
                      </option>
                      <option value={TransactionType.TRADING}>Trading</option>
                    </Select>
                  </FormControl>

                  <FormControl isRequired flex={1}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="600"
                      color="gray.600"
                      mb={1.5}
                    >
                      Amount
                    </FormLabel>
                    <InputGroup size="sm">
                      <InputLeftAddon
                        bg="gray.100"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRightRadius="0"
                        borderLeftRadius="md"
                        fontSize="sm"
                        color="gray.600"
                        fontWeight="600"
                        px={3}
                      >
                        ₹
                      </InputLeftAddon>
                      <NumberInput
                        min={0}
                        // step={0.01}
                        value={amount}
                        onChange={(valueString) => {
                          if (
                            valueString === "" ||
                            /^\d*\.?\d*$/.test(valueString)
                          ) {
                            setAmount(valueString);
                          }
                        }}
                        w="100%"
                      >
                        <NumberInputField
                          fontSize="sm"
                          borderLeftRadius="0"
                          borderRightRadius="md"
                          placeholder="0.00"
                          border="1px solid"
                          borderColor="gray.300"
                          _focus={{
                            borderColor: "#0b8e50",
                            boxShadow: "0 0 0 1px #0b8e50",
                          }}
                        />
                      </NumberInput>
                    </InputGroup>
                  </FormControl>
                </HStack>

                {/* Notes */}
                <FormControl>
                  <HStack justify="space-between" mb={1.5}>
                    <FormLabel
                      fontSize="xs"
                      fontWeight="600"
                      color="gray.600"
                      m={0}
                    >
                      Notes
                    </FormLabel>
                    <Text fontSize="10px" color="gray.400" fontWeight="500">
                      Max 200 chars
                    </Text>
                  </HStack>
                  <Textarea
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    maxLength={200}
                    minH="80px"
                    maxH="100px"
                    resize="none"
                    fontSize="sm"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.300"
                    placeholder="Advance for parts, invoice numbers, etc."
                    _focus={{
                      borderColor: "#0b8e50",
                      boxShadow: "0 0 0 1px #0b8e50",
                    }}
                  />
                </FormControl>

                {/* Submit Button */}
                <Button
                  w="100%"
                  bg="#0b8e50"
                  color="white"
                  borderRadius="md"
                  fontSize="sm"
                  fontWeight="600"
                  size="md"
                  leftIcon={isSubmitting ? <Spinner size="xs" /> : <FaBolt />}
                  _hover={{ bg: "#097743" }}
                  _active={{ bg: "#086838" }}
                  isDisabled={
                    isSubmitting ||
                    !transactionType ||
                    !amount ||
                    Number(amount) <= 0
                  }
                  onClick={handleSubmit}
                >
                  {isSubmitting ? "Generating..." : "Generate Payment Link"}
                </Button>
              </VStack>
            </Box>

            {/* ── Right Panel: Generated Links ─────────────────── */}
            <Box
              w={{ base: "100%", md: "60%" }}
              bg="white"
              display="flex"
              flexDirection="column"
              overflow="hidden"
            >
              {/* Section Header */}
              <HStack
                justify="space-between"
                align="center"
                px={5}
                py={4}
                bg="white"
                borderBottom="1px solid"
                borderColor="gray.200"
                h="72px"
                flexShrink={0}
              >
                <Box>
                  <Text
                    fontSize="15px"
                    fontWeight="700"
                    color="gray.800"
                    lineHeight="1.2"
                  >
                    Generated Links
                  </Text>
                  <Text fontSize="12px" color="gray.500" mt={0.5}>
                    Previously created payment links for this enquiry.
                  </Text>
                </Box>
                <Badge
                  bg={sortedLinks.length > 0 ? "#e8f5ee" : "gray.100"}
                  color={sortedLinks.length > 0 ? "#0b8e50" : "gray.500"}
                  borderRadius="md"
                  px={2.5}
                  py={1}
                  fontSize="11px"
                  fontWeight="700"
                  flexShrink={0}
                >
                  {sortedLinks.length}{" "}
                  {sortedLinks.length === 1 ? "LINK" : "LINKS"}
                </Badge>
              </HStack>

              {/* Links List */}
              <VStack
                ref={listRef}
                align="stretch"
                spacing={3}
                p={4}
                overflowY="auto"
                flex="1"
              >
                {sortedLinks.length === 0 ? (
                  <VStack
                    flex="1"
                    minH="240px"
                    justify="center"
                    color="gray.400"
                    spacing={3}
                  >
                    <Icon as={FaRegFileAlt} boxSize="36px" color="gray.300" />
                    <Text fontSize="sm" fontWeight="500" textAlign="center">
                      No payment links created for this enquiry!
                    </Text>
                  </VStack>
                ) : (
                  sortedLinks.map((link) => {
                      const linkId = link._id || link.zohoPaymentLinkId;
                      const isHighlighted = lastCreatedLinkId === linkId;

                      return (
                        <Box
                          key={linkId}
                          bg="gray.50"
                          border="1px solid"
                          borderColor={isHighlighted ? "#0b8e50" : "gray.200"}
                          boxShadow={isHighlighted ? "0 0 0 1px #0b8e50" : "none"}
                          borderRadius="lg"
                        p={3}
                        pl={4}
                        position="relative"
                        transition="box-shadow 0.2s ease"
                        _hover={{ boxShadow: "sm" }}
                      >
                        {/* Left accent bar */}
                        <Box
                          position="absolute"
                          left={0}
                          top={0}
                          bottom={0}
                          w="3px"
                          bg="#0b8e50"
                        />

                        {/* Amount + Badge + Action Icons */}
                        <HStack spacing={2} mb={2}>
                          <Text
                            fontSize="lg"
                            fontWeight="bold"
                            color="gray.800"
                          >
                            {formatCurrency(link.amount)}
                          </Text>
                          <Badge
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            color="gray.600"
                            borderRadius="sm"
                            fontSize="10px"
                            fontWeight="600"
                            letterSpacing="0.04em"
                            px={2}
                            py={0.5}
                          >
                            {link.transactionType.toUpperCase()}
                          </Badge>
                          <Spacer />
                          <Tooltip
                            label={
                              copiedLinkId === linkId ? "Copied!" : "Copy Link"
                            }
                            placement="top"
                            hasArrow
                            fontSize="xs"
                          >
                            <IconButton
                              aria-label="Copy Link"
                              icon={<Icon as={FaCopy} boxSize="15px" />}
                              size="sm"
                              variant="ghost"
                              color={
                                copiedLinkId === linkId ? "#0b8e50" : "gray.500"
                              }
                              _hover={{ color: "#0b8e50", bg: "gray.100" }}
                              onClick={() => handleCopy(link)}
                            />
                          </Tooltip>
                          <Tooltip
                            label="Open Link"
                            placement="top"
                            hasArrow
                            fontSize="xs"
                          >
                            <IconButton
                              aria-label="Open Link"
                              icon={
                                <Icon as={FaExternalLinkAlt} boxSize="14px" />
                              }
                              size="sm"
                              variant="ghost"
                              color="gray.500"
                              _hover={{ color: "#0b8e50", bg: "gray.100" }}
                              onClick={() => handleOpen(link.url)}
                            />
                          </Tooltip>
                        </HStack>

                        {/* Metadata */}
                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                          <MetadataItem
                            label="CREATED BY"
                            value={link.createdByUserName}
                          />
                          <MetadataItem
                            label="NOTES"
                            value={link.notes}
                            showTooltip
                          />
                          <MetadataItem
                            label="CREATED DATE"
                            value={formatPaymentLinkDate(link.createdAt)}
                          />
                          <MetadataItem
                            label="EXPIRY DATE"
                            value={formatPaymentLinkDate(link.expiryDate)}
                          />
                        </SimpleGrid>
                      </Box>
                    );
                  })
                )}
              </VStack>
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const MetadataItem = ({
  label,
  value,
  showTooltip,
}: {
  label: string;
  value: string | undefined;
  showTooltip?: boolean;
}) => (
  <Box minW={0}>
    <Text
      fontSize="10px"
      color="gray.500"
      fontWeight="700"
      letterSpacing="0.05em"
      mb={0.5}
    >
      {label}
    </Text>
    {showTooltip ? (
      <Tooltip
        label={value || "-"}
        placement="top"
        hasArrow
        bg="gray.700"
        color="white"
        fontSize="xs"
        borderRadius="md"
        maxW="280px"
        isDisabled={!value || value.length <= 30}
      >
        <Text
          fontSize="xs"
          fontWeight="medium"
          color="gray.800"
          noOfLines={1}
          cursor={value && value.length > 30 ? "pointer" : "default"}
        >
          {value || "-"}
        </Text>
      </Tooltip>
    ) : (
      <Text fontSize="xs" fontWeight="medium" color="gray.800" noOfLines={1}>
        {value || "-"}
      </Text>
    )}
  </Box>
);

export default PaymentLinksModal;
